import { computed, nextTick, ref, type ComputedRef, type Ref } from 'vue';
import type { UIMessage } from 'ai';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { getMessageText, parseMessageContent } from './messageUtils';
import getPageIdForScreen from './getPageIdForScreen';
import applyTextEdit, {
  applyTextEditToScreenYaml
} from '../../edit-agent/lib/applyTextEdit';

type PatchState = 'accepted' | 'rejected' | 'pending';

type LiteEdit = {
  messageId: string;
  editIndex: number;
  screenYaml: string;
  screenId: string;
  pageId: string;
  canApply: boolean;
};

type LiteReview = {
  allGroups: Array<{
    screenId: string;
    pageId: string;
  }>;
  allEdits: LiteEdit[];
  pendingEdits: LiteEdit[];
};

type LiteReviewStatus = 'pending' | 'accepted' | 'rejected' | 'mixed';

const emptyLiteReview: LiteReview = {
  allGroups: [],
  allEdits: [],
  pendingEdits: []
};

type Params = {
  messages: Ref<UIMessage[]>;
  yaml: ComputedRef<string | undefined>;
  patchStates: ComputedRef<Record<string, PatchState> | undefined>;
  messageCheckpoints: ComputedRef<Record<string, Record<number, string>>>;
  onPreviewPatch: (screenYaml: string) => void;
  onClearPreviewPatch: (screenId: string, keepDiff?: boolean) => void;
  onViewPatch: (pageId: string, screenId: string) => void;
  onAcceptPatch: (
    messageId: string,
    editIndex: number,
    screenYaml: string
  ) => void;
  onRejectPatch: (messageId: string, editIndex: number) => void;
  onRevertScreen: (
    screenId: string,
    messageId: string,
    editIndex: number
  ) => void;
};

function parseEditPayload(raw: string): any | null {
  try {
    const text = raw.trim();
    if (!text) return null;
    if (text.startsWith('{') || text.startsWith('[')) {
      return JSON.parse(text);
    }
    return yamlLoad(text);
  } catch {
    return null;
  }
}

/** Convert raw AI output (JSON or YAML) to YAML block style, matching PatchPreview.emitAccept. */
function toYaml(raw: string): string {
  const parsed = parseEditPayload(raw);
  if (!parsed) return raw;
  try {
    return yamlDump(parsed, { lineWidth: -1 });
  } catch {
    return raw;
  }
}

export type CurrentLiteSelection = {
  screenId: string;
  pageId: string;
  screenYaml: string;
  isValid: boolean;
  isFinal: boolean;
};

export type CurrentLiteSelectionDecision =
  | 'accepted'
  | 'rejected'
  | 'mixed'
  | null;
type EmitLiteSelectionOptions = {
  navigateOnly?: boolean;
};

export default function useLitePatchReview(params: Params) {
  const liteSelectedIndexByMessageId = ref<Record<string, number>>({});
  /** Maps screenId → screenYaml for every screen currently applied to the preview. */
  const liteActivePreviewScreenYamls = ref<Record<string, string>>({});
  /** Global "Show previous version" toggle: true = all screens show previous, false = show new changes. */
  const showPreviousVersion = ref(false);

  const compiledYamlObject = computed(() => {
    try {
      return params.yaml.value ? (yamlLoad(params.yaml.value) as any) : null;
    } catch {
      return null;
    }
  });

  // Build per-message lite review data from assistant edit chunks.
  const liteReviewByMessageId = computed<Record<string, LiteReview>>(() => {
    const next: Record<string, LiteReview> = {};
    const compiled = compiledYamlObject.value;
    const states = params.patchStates.value ?? {};

    for (const message of params.messages.value) {
      if (message.role !== 'assistant') continue;

      const allEdits: LiteEdit[] = [];
      const pendingEdits: LiteEdit[] = [];
      const groupedAll = new Map<string, LiteReview['allGroups'][number]>();
      const chunks = parseMessageContent(getMessageText(message));
      // Chain edits targeting the same screen: a later text-edit composes on
      // top of the prior edit's screen yaml rather than re-deriving from the
      // original compiled yaml (which would make only the last edit stick).
      const latestScreenYamlByScreenId: Record<string, string> = {};

      for (const chunk of chunks) {
        if (!chunk.complete) continue;
        if (chunk.type !== 'edit' && chunk.type !== 'text-edit') continue;

        const parsed = parseEditPayload(chunk.content);
        let screenId: string | undefined;
        let screenYaml: string;

        if (chunk.type === 'edit') {
          screenId =
            parsed && typeof parsed.id === 'string' ? parsed.id : undefined;
          screenYaml = chunk.content;
        } else {
          // text-edit: resolve to full screen YAML. Base priority: in-message
          // chain → checkpoint (pre-edit snapshot) → live yaml. Checkpoint-first
          // keeps a matched edit matching after the preview/accept mutates the buffer.
          screenId =
            parsed && typeof parsed.screenId === 'string'
              ? parsed.screenId
              : undefined;
          if (screenId) {
            const prior = latestScreenYamlByScreenId[screenId];
            const checkpoint =
              params.messageCheckpoints.value[message.id]?.[chunk.index];
            const hasCheckpoint =
              typeof checkpoint === 'string' && checkpoint.trim().length > 0;

            let resolved = '';
            if (prior) {
              const result = applyTextEditToScreenYaml(
                prior,
                parsed.oldText,
                parsed.newText
              );
              resolved = result.found ? result.screenYaml : '';
            } else {
              if (hasCheckpoint) {
                const fromCheckpoint = applyTextEditToScreenYaml(
                  checkpoint,
                  parsed.oldText,
                  parsed.newText
                );
                if (fromCheckpoint.found) {
                  resolved = fromCheckpoint.screenYaml;
                }
              }
              // No checkpoint, or checkpoint lacked oldText: fall back to the live
              // buffer (a checkpoint hit above is sticky, so this never regresses).
              if (!resolved && params.yaml.value) {
                const result = applyTextEdit(
                  params.yaml.value,
                  screenId,
                  parsed.oldText,
                  parsed.newText
                );
                if (result.found) {
                  resolved = result.screenYaml;
                }
              }
            }

            screenYaml = resolved;
          } else {
            screenYaml = '';
          }
        }

        if (!screenId) {
          continue;
        }

        const pageId = compiled
          ? getPageIdForScreen(screenId, compiled) || ''
          : '';
        const state = states[`${message.id}_${chunk.index}`] ?? 'pending';

        // Keep the screen in the navigator even when this edit cannot be replayed.
        if (!groupedAll.has(screenId)) {
          groupedAll.set(screenId, {
            screenId,
            pageId
          });
        }

        if (!screenYaml) {
          const unresolvedEdit: LiteEdit = {
            messageId: message.id,
            editIndex: chunk.index,
            screenYaml: '',
            screenId,
            pageId,
            canApply: false
          };
          allEdits.push(unresolvedEdit);
          if (state === 'pending') pendingEdits.push(unresolvedEdit);
          continue;
        }

        if (screenYaml) latestScreenYamlByScreenId[screenId] = screenYaml;

        const edit: LiteEdit = {
          messageId: message.id,
          editIndex: chunk.index,
          screenYaml,
          screenId,
          pageId,
          canApply: true
        };
        allEdits.push(edit);
        if (state === 'pending') pendingEdits.push(edit);
      }

      next[message.id] = {
        allGroups: Array.from(groupedAll.values()),
        allEdits,
        pendingEdits
      };
    }

    return next;
  });

  function getLiteReviewForMessage(message: UIMessage): LiteReview {
    return liteReviewByMessageId.value[message.id] ?? emptyLiteReview;
  }

  function getLiteReviewStatusForMessage(message: UIMessage): LiteReviewStatus {
    const states = params.patchStates.value ?? {};
    const { allEdits } =
      liteReviewByMessageId.value[message.id] ?? emptyLiteReview;
    if (!allEdits.length) return 'pending';

    let acceptedCount = 0;
    let rejectedCount = 0;
    let pendingCount = 0;

    for (const edit of allEdits) {
      const state = states[`${edit.messageId}_${edit.editIndex}`] ?? 'pending';
      if (state === 'accepted') acceptedCount++;
      else if (state === 'rejected') rejectedCount++;
      else pendingCount++;
    }

    if (pendingCount > 0) return 'pending';
    if (acceptedCount > 0 && rejectedCount === 0) return 'accepted';
    if (rejectedCount > 0 && acceptedCount === 0) return 'rejected';
    return 'mixed';
  }

  function getLiteNavigatorIndex(message: UIMessage): number {
    const total = getLiteReviewForMessage(message).allGroups.length;
    if (total <= 0) return 0;

    const current = liteSelectedIndexByMessageId.value[message.id] ?? 0;
    const clamped = Math.max(0, Math.min(current, total - 1));
    if (current !== clamped) {
      liteSelectedIndexByMessageId.value[message.id] = clamped;
    }
    return clamped;
  }

  function getCurrentLiteSelection(
    message: UIMessage
  ): CurrentLiteSelection | null {
    const review = getLiteReviewForMessage(message);
    if (!review.allGroups.length) return null;

    const index = getLiteNavigatorIndex(message);
    const selected = review.allGroups[index];
    if (!selected) return null;

    const matching = review.pendingEdits.filter(
      edit => edit.screenId === selected.screenId
    );
    const selectedPendingEdit = matching.slice(-1)[0];

    const isFinal = !selectedPendingEdit;
    const screenYaml = selectedPendingEdit?.screenYaml ?? '';
    const isValid =
      !!selectedPendingEdit &&
      parseEditPayload(selectedPendingEdit.screenYaml) !== null;

    return {
      screenId: selected.screenId,
      pageId: selected.pageId,
      screenYaml,
      isValid,
      isFinal
    };
  }

  function getCurrentLiteSelectionDecision(
    message: UIMessage
  ): CurrentLiteSelectionDecision {
    const review = getLiteReviewForMessage(message);
    if (!review.allGroups.length) return null;

    const states = params.patchStates.value ?? {};
    const index = getLiteNavigatorIndex(message);
    const selected = review.allGroups[index];
    if (!selected) return null;

    const selectedEdits = review.allEdits.filter(
      edit => edit.screenId === selected.screenId
    );
    if (!selectedEdits.length) return null;

    let acceptedCount = 0;
    let rejectedCount = 0;
    let pendingCount = 0;

    for (const edit of selectedEdits) {
      const state = states[`${edit.messageId}_${edit.editIndex}`] ?? 'pending';
      if (state === 'accepted') acceptedCount++;
      else if (state === 'rejected') rejectedCount++;
      else pendingCount++;
    }

    if (pendingCount > 0) return null;
    if (acceptedCount > 0 && rejectedCount === 0) return 'accepted';
    if (rejectedCount > 0 && acceptedCount === 0) return 'rejected';
    return 'mixed';
  }

  function getShowPreviousVersion(): boolean {
    return showPreviousVersion.value;
  }

  function setShowPreviousVersion(value: boolean): void {
    showPreviousVersion.value = value;
    if (value) {
      // Show previous: unapply the preview patches so the runtime renders the
      // pre-edit text, but keep the diff snapshots so the inline word diff can
      // highlight the words that would be removed.
      for (const screenId of Object.keys(liteActivePreviewScreenYamls.value)) {
        params.onClearPreviewPatch(screenId, true);
      }
      return;
    }
    // Show new: re-apply all tracked previews
    for (const screenYaml of Object.values(
      liteActivePreviewScreenYamls.value
    )) {
      params.onPreviewPatch(screenYaml);
    }
  }

  function viewCurrentSelection(message: UIMessage, review?: LiteReview) {
    const currentReview = review ?? getLiteReviewForMessage(message);
    const index = getLiteNavigatorIndex(message);
    const selected = currentReview.allGroups[index];
    if (selected) {
      params.onViewPatch(selected.pageId, selected.screenId);
    }
  }

  // Keep preview highlight and navigator selection in sync.
  function emitLiteSelection(
    message: UIMessage,
    options: EmitLiteSelectionOptions = {}
  ) {
    const review = getLiteReviewForMessage(message);
    if (options.navigateOnly) {
      viewCurrentSelection(message, review);
      return;
    }
    const states = params.patchStates.value ?? {};

    // Build the new set of pending screen yamls (last edit per screen wins)
    const newScreenYamls: Record<string, string> = {};
    for (const edit of review.pendingEdits) {
      if (!edit.canApply || !edit.screenYaml) continue;
      newScreenYamls[edit.screenId] = edit.screenYaml;
    }

    const screenAccepted = (screenId: string): boolean =>
      review.allEdits.some(
        e =>
          e.screenId === screenId &&
          states[`${e.messageId}_${e.editIndex}`] === 'accepted'
      );

    // Clear screens that were previously previewed but are no longer pending.
    // Skip accepted screens: clearPreviewPatch reverts Monaco to the original base
    // yaml, which would undo the accepted change visually.
    for (const screenId of Object.keys(liteActivePreviewScreenYamls.value)) {
      if (!(screenId in newScreenYamls)) {
        if (screenAccepted(screenId)) continue;
        params.onClearPreviewPatch(screenId);
      }
    }

    if (!review.pendingEdits.length) {
      liteActivePreviewScreenYamls.value = {};
      return;
    }

    liteActivePreviewScreenYamls.value = newScreenYamls;

    const showNew = !showPreviousVersion.value;
    if (showNew) {
      for (const screenYaml of Object.values(newScreenYamls)) {
        params.onPreviewPatch(screenYaml);
      }
    }

    // Navigate (scroll) to the currently selected screen
    viewCurrentSelection(message, review);
  }

  function onLitePrev(message: UIMessage) {
    const review = getLiteReviewForMessage(message);
    const total = review.allGroups.length;
    if (total <= 0) return;

    const current = getLiteNavigatorIndex(message);
    liteSelectedIndexByMessageId.value[message.id] =
      total === 1 ? 0 : (current - 1 + total) % total;
    emitLiteSelection(message);
  }

  function onLiteNext(message: UIMessage) {
    const review = getLiteReviewForMessage(message);
    const total = review.allGroups.length;
    if (total <= 0) return;

    const current = getLiteNavigatorIndex(message);
    liteSelectedIndexByMessageId.value[message.id] =
      total === 1 ? 0 : (current + 1) % total;
    emitLiteSelection(message);
  }

  function onLiteAcceptAll(message: UIMessage) {
    const { pendingEdits } = getLiteReviewForMessage(message);
    if (!pendingEdits.length) return;

    // For chained edits on the same screen, the last applicable yaml wins.
    const latestByScreen = new Map<string, string>();
    for (const edit of pendingEdits) {
      if (edit.canApply && edit.screenYaml)
        latestByScreen.set(edit.screenId, edit.screenYaml);
    }
    for (const edit of pendingEdits) {
      const yaml = latestByScreen.get(edit.screenId);
      if (!yaml) continue;
      params.onAcceptPatch(edit.messageId, edit.editIndex, toYaml(yaml));
    }
    nextTick(() => emitLiteSelection(message));
  }

  function getPendingEditsForSelectedScreen(message: UIMessage): LiteEdit[] {
    const review = getLiteReviewForMessage(message);
    if (!review.allGroups.length) return [];

    const index = getLiteNavigatorIndex(message);
    const selected = review.allGroups[index];
    if (!selected) return [];

    return review.pendingEdits.filter(
      edit => edit.screenId === selected.screenId
    );
  }

  function advanceToNextPendingScreen(message: UIMessage) {
    const review = getLiteReviewForMessage(message);
    if (!review.pendingEdits.length) return;

    const current = getLiteNavigatorIndex(message);
    const total = review.allGroups.length;
    for (let i = 1; i <= total; i++) {
      const idx = (current + i) % total;
      const screenId = review.allGroups[idx]?.screenId;
      if (review.pendingEdits.some(e => e.screenId === screenId)) {
        liteSelectedIndexByMessageId.value[message.id] = idx;
        return;
      }
    }
  }

  function onLiteAcceptCurrent(message: UIMessage) {
    const selectedPendingEdits = getPendingEditsForSelectedScreen(
      message
    ).filter(edit => edit.canApply && !!edit.screenYaml);
    if (!selectedPendingEdits.length) return;

    for (const edit of selectedPendingEdits) {
      params.onAcceptPatch(
        edit.messageId,
        edit.editIndex,
        toYaml(edit.screenYaml)
      );
    }
    nextTick(() => {
      advanceToNextPendingScreen(message);
      emitLiteSelection(message);
    });
  }

  function onLiteRejectCurrent(message: UIMessage) {
    const selectedPendingEdits = getPendingEditsForSelectedScreen(message);
    if (!selectedPendingEdits.length) return;

    for (const edit of selectedPendingEdits) {
      params.onRejectPatch(edit.messageId, edit.editIndex);
    }
    nextTick(() => {
      advanceToNextPendingScreen(message);
      emitLiteSelection(message);
    });
  }

  function onLiteDoneReviewing(message: UIMessage) {
    const review = getLiteReviewForMessage(message);
    if (!review.pendingEdits.length) return;

    for (const edit of review.pendingEdits) {
      params.onRejectPatch(edit.messageId, edit.editIndex);
    }
    nextTick(() => emitLiteSelection(message));
  }

  function onLiteRejectAll(message: UIMessage) {
    const { allEdits } = getLiteReviewForMessage(message);
    if (!allEdits.length) return;

    const states = params.patchStates.value ?? {};

    // Revert using the first edit per screen as the anchor — that edit's snapshot
    // holds the pre-message yaml, restoring the screen to before this message touched it.
    const firstEditByScreen = new Map<string, LiteEdit>();
    for (const edit of allEdits) {
      if (!firstEditByScreen.has(edit.screenId))
        firstEditByScreen.set(edit.screenId, edit);
    }
    for (const [screenId, edit] of firstEditByScreen) {
      const wasAccepted = allEdits.some(
        e =>
          e.screenId === screenId &&
          states[`${e.messageId}_${e.editIndex}`] === 'accepted'
      );
      if (wasAccepted)
        params.onRevertScreen(edit.screenId, edit.messageId, edit.editIndex);
    }

    for (const edit of allEdits) {
      params.onRejectPatch(edit.messageId, edit.editIndex);
    }
    nextTick(() => emitLiteSelection(message));
  }

  function clearActiveLitePreview() {
    for (const screenId of Object.keys(liteActivePreviewScreenYamls.value)) {
      params.onClearPreviewPatch(screenId);
    }
    liteActivePreviewScreenYamls.value = {};
  }

  return {
    liteReviewByMessageId,
    getLiteReviewForMessage,
    getLiteReviewStatusForMessage,
    getLiteNavigatorIndex,
    getCurrentLiteSelection,
    getCurrentLiteSelectionDecision,
    getShowPreviousVersion,
    setShowPreviousVersion,
    emitLiteSelection,
    onLitePrev,
    onLiteNext,
    onLiteAcceptCurrent,
    onLiteRejectCurrent,
    onLiteAcceptAll,
    onLiteRejectAll,
    onLiteDoneReviewing,
    clearActiveLitePreview
  };
}
